<script setup lang="ts">
import { matchesHierarchyFilter } from "@/modules/base_price/utils/hierarchyFilter";
import { computed, ref } from "vue";
import { formatNumber, formatNumberForScale } from "chartjs-graph";
import { useBasePriceStore } from "@/modules/base_price/store";
import { budgetAllocationIssue } from "@/modules/base_price/utils/budgetAllocation";
import ResolvedNodeDTO = Modules.BasePrice.Data.Resolved.ResolvedNodeDTO;

defineOptions({ name: "PlanHierarchyRow" });

/**
 * A screen (Asset) as a tree row under the node that carries it. Not a real
 * hierarchy node: `hierarchy_node_id` is a negative placeholder that exists
 * only to key the row, and `assetId` holds the real Asset id, which lives in
 * its own id space. It *is* selectable — a screen carries its own
 * seasonality, fill and manual CPM (see ResolvedAssetDTO) — but its dimmed
 * state follows its real parent's subtree membership, since its own
 * synthetic id is not something the store ever tracks.
 */
export type HierarchyTreeNode = ResolvedNodeDTO & {
	children: HierarchyTreeNode[];
	isAsset?: boolean;
	assetId?: number;
	/** A screen's key — searched by the sidebar's filter, never shown. */
	nameClean?: string;
};

const props = withDefaults(
	defineProps<{
		node: HierarchyTreeNode;
		depth: number;
		metric: "budget" | "cpm";
		/**
		 * The sidebar's filter, lower-cased, when one is typed. The tree it hands
		 * down is already cut to the matches and their ancestors; here it only
		 * opens every level and tells a match from the path leading to it.
		 */
		filter?: string;
	}>(),
	{ filter: "" }
);

const isMatch = computed(() => matchesHierarchyFilter(props.node, props.filter));

const store = useBasePriceStore();
const hasChildren = computed(() => props.node.children.length > 0);

/** While filtering every level is open, so no match hides behind a closed one. */
const isOpen = computed(() => props.filter !== "" || expanded.value);

/**
 * A node whose children are *only* screens starts collapsed, so the tree
 * opens as a hierarchy of groups rather than a wall of every screen in the
 * portfolio — the screens are still one click away. A node with any real
 * child node stays open, including a mixed one that carries screens
 * alongside children: collapsing that would hide real levels of the
 * hierarchy to tidy away the screens sitting next to them.
 */
const expanded = ref(
	!(
		props.node.children.length > 0 &&
		props.node.children.every((child) => child.isAsset)
	)
);

/**
 * A screen row highlights on its own Asset id; a node row highlights only
 * while no screen beneath it is selected, so exactly one row in the tree is
 * ever marked as what the detail panel is editing.
 */
const isSelected = computed(() =>
	props.node.isAsset
		? store.selectedAssetId === props.node.assetId
		: store.selectedAssetId === null &&
			store.selectedNodeId === props.node.hierarchy_node_id
);
/**
 * Everything outside what the detail panel is editing is dimmed. A node's
 * "subtree" is the node and everything below it; a screen has nothing below
 * it, so with a screen selected only that screen stays lit — its sibling
 * screens and the node carrying it dim, exactly as a node's siblings and
 * ancestors do when a node is selected.
 */
const isDimmed = computed(() => {
	if (store.selectedAssetId !== null) {
		return !(props.node.isAsset && props.node.assetId === store.selectedAssetId);
	}

	return !store.isInSelectedSubtree(
		props.node.isAsset
			? (props.node.parent_id ?? props.node.hierarchy_node_id)
			: props.node.hierarchy_node_id
	);
});
const onClick = () => {
	if (props.node.isAsset) {
		// A screen is only meaningful inside the node carrying it, so the two
		// are selected together — selectAsset() sets the node first.
		if (props.node.parent_id !== null && props.node.assetId !== undefined) {
			store.selectAsset(props.node.parent_id, props.node.assetId);
		}
		return;
	}

	store.selectNode(props.node.hierarchy_node_id);
};

/**
 * What the node was handed rather than what its children add up to — see
 * PlanEditTarget.budget — so an under-committed node shows its shortfall in
 * the gauge instead of quietly shrinking to match.
 */
const primaryValue = computed(() =>
	formatNumberForScale("money", props.node.allotted_budget)
);
const secondaryValue = computed(() => formatNumber(props.node.cpm, "money"));

/**
 * Same revenue ÷ budget gauge shown on the Fill & CPM tab's header panel —
 * exposed as a column here too so it's visible for every node at a glance,
 * not just the one currently selected. Same color/threshold rules: green
 * "at budget" (~0.2% tolerance), orange under, blue over.
 */
const budgetPct = computed(() => {
	const budget = props.node.allotted_budget;
	const gp = budget > 0 ? props.node.revenue / budget : 0;
	const onBudget = Math.abs(gp - 1) < 0.002;

	return {
		text: `${Math.round(gp * 100)}%`,
		color: onBudget
			? "rgb(var(--v-theme-success))"
			: gp < 1
				? "rgb(var(--v-theme-warning))"
				: "rgb(var(--v-theme-info))",
	};
});

/**
 * Effective CPM per month (revenue[i] ÷ sellable[i]/1000) — not stored by
 * the resolver, since it's cheaply derivable client-side from the monthly
 * arrays it already sends. Months with no sellable impressions are excluded
 * from min/max (a 0 there isn't a "low CPM", it's undefined).
 */
const monthlyCpm = computed<number[]>(() =>
	props.node.monthly_revenue
		.map((revenue, i) => {
			const sellable = props.node.monthly_sellable_impressions[i];
			return sellable > 0 ? revenue / (sellable / 1000) : null;
		})
		.filter((value): value is number => value !== null)
);

const cpmStats = computed(() => ({
	avg: props.node.cpm,
	min: monthlyCpm.value.length ? Math.min(...monthlyCpm.value) : 0,
	max: monthlyCpm.value.length ? Math.max(...monthlyCpm.value) : 0,
}));

/**
 * The red dot, covering both ways a target can promise away more than it has:
 * across its children (Budget split) and across its own twelve months
 * (Seasonality, once a month is pinned to an exact amount). A pin is set
 * against the budget the target had at the time, and a split changed higher
 * up can leave it asking for more than now arrives — the same kind of
 * conflict, one dimension over, so it earns the same marker.
 *
 * Deliberately not contextual to the open tab, unlike markColor below: an
 * over-committed node is wrong wherever you happen to be looking.
 */
const allocationIssue = computed<string | null>(() => {
	const budgetIssue = budgetAllocationIssue(props.node);
	if (budgetIssue === "over") {
		return "The fixed and % shares on this node's children add up to more than its budget — check its Budget split tab";
	}
	if (budgetIssue === "under") {
		return "This node hasn't assigned its entire budget to its children — check its Budget split tab";
	}

	if (!props.node.season_fully_allocated) {
		return "The months pinned here don't fit this node's budget — check its Monthly Budget Distribution tab";
	}

	if (!props.node.days_fully_allocated) {
		return "The days pinned here don't fit their month's budget — check its Monthly Budget Distribution tab";
	}

	if (!props.node.cpm_within_bounds) {
		return "A hand-set CPM here sits outside this node's price bounds — check its CPM & Fill tab";
	}

	// Plan-level rather than per-book: two books promising the same screen is
	// wrong whichever book you happen to be looking at.
	if (
		props.node.isAsset
			? props.node.assetId !== undefined &&
				store.overReservedAssetIds.has(props.node.assetId)
			: store.nodeOverReserved(props.node.hierarchy_node_id)
	) {
		return props.node.isAsset
			? "More than one book reserves this screen's whole inventory in a month — check the Impression share tab"
			: "A screen here is reserved by more than one book beyond its inventory — check the Impression share tab";
	}

	// Per book, and only where it is set: see shareBudgetMismatch().
	const shareIssue = props.node.isAsset
		? props.node.assetId !== undefined
			? store.shareBudgetIssue("asset", props.node.assetId)
			: null
		: store.shareBudgetIssue("node", props.node.hierarchy_node_id);

	if (shareIssue === "budget_without_share") {
		return "This reserves nothing all year but still gets budget, which it can't earn — check the Impression share tab";
	}
	if (shareIssue === "share_without_budget") {
		return "This reserves impressions but has no budget, so they go for €0 — check the Impression share tab";
	}

	return null;
});

/**
 * Contextual to whichever tab is open in PlanNodeDetailPanel
 * (store.activeDetailTab) — a node marks orange for its split/fixed-budget
 * override while viewing Budget split, its season override while viewing
 * Seasonality, and its fill/manual-CPM override while viewing Fill & CPM,
 * rather than always showing the Budget split state regardless of what is
 * actually being looked at. A tab with nothing editable behind it has
 * nothing to check, so it never marks anything.
 */
const markColor = computed(() => {
	const hasOwnOverride =
		store.activeDetailTab === "split"
			? props.node.split_own || props.node.fixed_budget_own
			: store.activeDetailTab === "seasonality"
				? props.node.season_own || props.node.days_own
				: store.activeDetailTab === "fill_cpm" || store.activeDetailTab === "cpm_per_month"
					? props.node.fill_own ||
						props.node.manual_cpm_own ||
						props.node.cpm_bounds_own ||
						props.node.day_pricing_own
					: store.activeDetailTab === "impression_share"
						? props.node.impression_share_own
						: false;

	return hasOwnOverride ? "rgb(var(--v-theme-warning))" : "transparent";
});
</script>

<template>
	<div>
		<div
			class="tree-row"
			:class="{
				'tree-row--selected': isSelected,
				'tree-row--dimmed': isDimmed,
				'tree-row--asset': node.isAsset,
			}"
			:style="{ borderLeftColor: markColor }"
			@click="onClick"
		>
			<!--
				Indentation lives on this wrapper, not on the row, so nesting
				eats into the label's own width instead of pushing the metric
				columns to the right — they stay in one grid at every depth, and
				a deeply nested or long name ellipsizes inside the 310px cap.
			-->
			<div class="tree-label" :style="{ paddingLeft: `${depth * 18 + 12}px` }">
				<!--
				The open/closed state is carried by a class on this span, which
				rotates a single, never-changing chevron — deliberately not two
				icons swapped by a binding. Font Awesome runs in SVG mode with
				dom.watch() (see plugins/index.ts), which replaces the icon
				element with an <svg> as soon as it is inserted; any later class
				or prop change Vue makes then lands on a node that is no longer
				in the document, so a swapped icon renders once and never flips
				again. Nothing here re-renders the icon, so nothing can go stale.
			-->
				<span
					class="tree-caret"
					:class="{ 'tree-caret--expanded': isOpen }"
					@click.stop="expanded = !expanded"
				>
					<!--
						Each icon sits in a span the v-if switches, never the <i>
						itself: Font Awesome swaps the <i> for an <svg> on insert, so
						Vue patching the <i> directly — as filtering does when a row
						gains or loses its children — reaches a node that is no
						longer in the document and crashes the whole tree.
					-->
					<span v-if="hasChildren" class="d-inline-flex"
						><i class="fa-regular fa-chevron-right"
					/></span>
					<span v-else-if="node.isAsset" class="d-inline-flex tree-asset-icon-wrap"
						><i class="fa-regular fa-display tree-asset-icon"
					/></span>
				</span>
				<span
					class="tree-col-level text-truncate"
					:class="{ 'font-weight-bold': isSelected }"
				>
					<span
						class="tree-name text-truncate"
						:class="{
							'tree-name--match': isMatch,
							'text-medium-emphasis': filter !== '' && !isMatch,
						}"
						>{{ node.name }}</span
					>
					<span
						v-if="allocationIssue"
						class="allocation-error-dot"
						:title="allocationIssue"
					/>
				</span>
			</div>
			<template v-if="metric === 'cpm'">
				<span class="tree-col-value tree-col-value--narrow">{{
					formatNumber(cpmStats.avg, "money")
				}}</span>
				<span class="tree-col-value tree-col-value--narrow">{{
					formatNumber(cpmStats.min, "money")
				}}</span>
				<span class="tree-col-value tree-col-value--narrow">{{
					formatNumber(cpmStats.max, "money")
				}}</span>
			</template>
			<template v-else>
				<span class="tree-col-value">{{ primaryValue }}</span>
				<span class="tree-col-value">{{ secondaryValue }}</span>
				<span
					class="tree-col-value tree-col-value--narrow"
					:style="{ color: budgetPct.color }"
					>{{ budgetPct.text }}</span
				>
			</template>
		</div>

		<template v-if="isOpen">
			<plan-hierarchy-row
				v-for="child in node.children"
				:key="child.hierarchy_node_id"
				:node="child"
				:depth="depth + 1"
				:metric="metric"
				:filter="filter"
			/>
		</template>
	</div>
</template>

<style scoped lang="scss">
// A filter match, set apart from the levels shown only to place it.
.tree-name--match {
	font-weight: 700;
	color: rgb(var(--v-theme-primary));
}

.tree-row {
	display: flex;
	align-items: center;
	gap: 8px;
	padding-top: 6px;
	padding-bottom: 6px;
	padding-right: 12px;
	border-left: 3px solid transparent;
	cursor: pointer;
}

// Caps the whole label column — indent, twisty and name together — at 310px
// and never lets it wrap. It still shrinks below that when the sidebar is
// narrower than the metric columns need; the cap only stops it growing.
.tree-label {
	flex: 1 1 auto;
	min-width: 0;
	max-width: 310px;
	display: flex;
	align-items: center;
	gap: 8px;
	overflow: hidden;
	white-space: nowrap;
}

.tree-row:hover {
	background: rgba(var(--v-theme-on-surface), 0.04);
}

.tree-row--selected {
	background: rgba(var(--v-theme-on-surface), 0.06);
}

.tree-row--dimmed {
	opacity: 0.4;
	transition: opacity 0.15s ease;
}

.tree-row--asset {
	cursor: default;
}

.tree-row--asset:hover {
	background: transparent;
}

.tree-asset-icon {
	font-size: 10px;
	color: rgba(var(--v-theme-on-surface), 0.4);
}

.tree-caret {
	width: 14px;
	flex: 0 0 auto;
	font-size: 10px;
	text-align: center;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

// Targets whatever the icon element currently is — Font Awesome swaps the
// <i> for an <svg> after insertion, and this has to keep working either way.
.tree-caret > * {
	display: inline-block;
	transition: transform 0.15s ease;
}

.tree-caret--expanded > * {
	transform: rotate(90deg);
}

// The screen icon shares the caret span but is not a twisty — it must not
// rotate with its parent's expanded state.
.tree-caret--expanded > .tree-asset-icon-wrap {
	transform: none;
}

// min-width: 0 on every step of the chain — a flex item defaults to
// min-width: auto, which refuses to shrink below its content and would let a
// long name push straight through the cap instead of ellipsizing.
.tree-name {
	min-width: 0;
}

.tree-col-level {
	flex: 1 1 auto;
	min-width: 0;
	font-size: 13px;
	display: flex;
	align-items: center;
	gap: 6px;
}

.tree-col-value {
	flex: 0 0 72px;
	text-align: right;
	font-size: 12.5px;
	font-variant-numeric: tabular-nums;
}

.tree-col-value--narrow {
	flex: 0 0 56px;
}

.allocation-error-dot {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: rgb(var(--v-theme-error));
	flex: 0 0 auto;
}

</style>
